import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import { useDashReport } from '../hooks/useDashReport'
import type { SegmentacionEspecialidadCiudadReport } from '../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const PALETA_COLORES = [
  { bg: 'rgba(5, 69, 126, 0.85)', border: '#05457e' },       // base
  { bg: 'rgba(4, 58, 107, 0.85)', border: '#043a6b' },       // darker
  { bg: 'rgba(7, 89, 158, 0.85)', border: '#07599e' },       // lighter
  { bg: 'rgba(10, 112, 192, 0.85)', border: '#0a70c0' },     // bright
  { bg: 'rgba(3, 45, 82, 0.85)', border: '#032d52' },        // darkest
  { bg: 'rgba(40, 130, 200, 0.85)', border: '#2882c8' },     // light
  { bg: 'rgba(14, 78, 135, 0.85)', border: '#0e4e87' },      // medium
  { bg: 'rgba(60, 150, 215, 0.85)', border: '#3c96d7' },     // lighter-2
  { bg: 'rgba(2, 35, 65, 0.85)', border: '#022341' },        // navy
  { bg: 'rgba(80, 165, 225, 0.85)', border: '#50a5e1' },     // sky
  { bg: 'rgba(20, 95, 155, 0.85)', border: '#145f9b' },      // steel
  { bg: 'rgba(100, 180, 235, 0.85)', border: '#64b4eb' },    // pale
  { bg: 'rgba(25, 60, 100, 0.85)', border: '#193c64' },      // deep
  { bg: 'rgba(120, 190, 240, 0.85)', border: '#78bef0' },    // lightest
  { bg: 'rgba(8, 50, 90, 0.85)', border: '#08325a' },        // dark-steel
  { bg: 'rgba(140, 200, 245, 0.85)', border: '#8cc8f5' },    // ice
]

const LOGO_URL = '/logo-tonal-negro.png'

function pct(total: number, base: number): number {
  return base > 0 ? Math.round((total / base) * 1000) / 10 : 0
}

export function PsiquiatrasEcuPage() {
  const { data, isLoading, error } = useDashReport<SegmentacionEspecialidadCiudadReport>('psiquiatras/ecuador')
  const [filtros, setFiltros] = useState<string[]>([])
  const [espActiva, setEspActiva] = useState<string>('todas')
  const [expanded, setExpanded] = useState(false)
  const [resumenExpanded, setResumenExpanded] = useState(true)
  const contentRef = useRef<HTMLDivElement>(null)
  const resumenRef = useRef<HTMLDivElement>(null)

  const todasSeleccionadas = filtros.length === 0
  const especialidadTodas = espActiva === 'todas'

  const toggleCiudad = useCallback((ciudad: string) => {
    setFiltros(prev => {
      if (ciudad === 'todas') return []
      const existe = prev.includes(ciudad)
      return existe ? prev.filter(f => f !== ciudad) : [...prev, ciudad]
    })
  }, [])

  const toggleEspecialidad = useCallback((nombre: string) => {
    setEspActiva(prev => (prev === nombre ? 'todas' : nombre))
    setFiltros([]) // reiniciar filtro de ciudad al cambiar de especialidad
  }, [])

  useEffect(() => {
    if (contentRef.current) {
      if (expanded) {
        contentRef.current.style.maxHeight = contentRef.current.scrollHeight + 'px'
        contentRef.current.style.opacity = '1'
      } else {
        contentRef.current.style.maxHeight = '0px'
        contentRef.current.style.opacity = '0'
      }
    }
  }, [expanded, filtros, espActiva])

  useEffect(() => {
    if (resumenRef.current) {
      if (resumenExpanded) {
        resumenRef.current.style.maxHeight = resumenRef.current.scrollHeight + 'px'
        resumenRef.current.style.opacity = '1'
      } else {
        resumenRef.current.style.maxHeight = '0px'
        resumenRef.current.style.opacity = '0'
      }
    }
  }, [resumenExpanded, filtros, espActiva])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#05457e' }} />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-32">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const segmentos = data.segmentos
  const especialidades = data.especialidades ?? []
  const detalle = data.detalle ?? []

  // detalle filtrado por la especialidad activa
  const detalleEsp = especialidadTodas
    ? detalle
    : detalle.filter(d => d.especialidad === espActiva)

  // segmentos agregados por ciudad para la especialidad activa (orden desc)
  const mapaCiudad: Record<string, number> = {}
  detalleEsp.forEach(d => { mapaCiudad[d.ciudad] = (mapaCiudad[d.ciudad] ?? 0) + d.total })
  const baseSeg = Object.entries(mapaCiudad)
    .map(([ciudad, total]) => ({ ciudad, total }))
    .sort((a, b) => b.total - a.total)
  const disponibles = new Set(baseSeg.map(s => s.ciudad))

  // aplicar filtro de ciudad
  const segFiltrados = todasSeleccionadas
    ? baseSeg
    : baseSeg.filter(s => filtros.includes(s.ciudad))
  const totalMostrado = segFiltrados.reduce((sum, s) => sum + s.total, 0)
  const ciudadesMostradas = segFiltrados.length

  const tituloResumen = todasSeleccionadas
    ? (especialidadTodas ? 'Resumen General' : espActiva)
    : filtros.length === 1 ? filtros[0] : `${filtros.length} ciudades`
  const etiqueta = especialidadTodas ? 'médicos' : espActiva.toLowerCase()
  const labelTotal = especialidadTodas ? 'Total Médicos' : `Total ${espActiva}`
  const especialidadesMostradas = especialidadTodas ? especialidades.length : 1

  // --- Datos gráfica por ciudad ---
  const labels = segFiltrados.map(s => s.ciudad)
  const valores = segFiltrados.map(s => s.total)
  const bgColors = segFiltrados.map((_s, idx) => PALETA_COLORES[idx % PALETA_COLORES.length].bg)
  const borderColors = segFiltrados.map((_s, idx) => PALETA_COLORES[idx % PALETA_COLORES.length].border)

  const barData = {
    labels,
    datasets: [{
      label: 'Total',
      data: valores,
      backgroundColor: bgColors,
      borderColor: borderColors,
      borderWidth: 1,
    }],
  }

  const barOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 80 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 },
        formatter: function (value: number) {
          return value.toLocaleString('es-EC')
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '5%',
        ticks: {
          callback: function (value: number | string) {
            return Number(value).toLocaleString('es-EC')
          },
        },
      },
    },
  }

  // --- Datos gráfica por especialidad (respeta filtro de ciudad, no el de especialidad) ---
  const detalleCiudad = todasSeleccionadas
    ? detalle
    : detalle.filter(d => filtros.includes(d.ciudad))
  const mapaEsp: Record<string, number> = {}
  detalleCiudad.forEach(d => { mapaEsp[d.especialidad] = (mapaEsp[d.especialidad] ?? 0) + d.total })
  const espLabels = especialidades.map(e => e.nombre).filter(n => mapaEsp[n] !== undefined)
  const espValores = espLabels.map(n => mapaEsp[n])

  const totalEsp = espValores.reduce((a, b) => a + b, 0)
  const espBarData = {
    labels: espLabels,
    datasets: [{
      label: 'Total',
      data: espValores,
      backgroundColor: espLabels.map((_l, idx) => PALETA_COLORES[idx % PALETA_COLORES.length].bg),
      borderColor: espLabels.map((_l, idx) => PALETA_COLORES[idx % PALETA_COLORES.length].border),
      borderWidth: 1,
    }],
  }

  const espBarOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 90 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 },
        formatter: function (value: number) {
          return value.toLocaleString('es-EC') + ' (' + pct(value, totalEsp) + '%)'
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '5%',
        ticks: {
          callback: function (value: number | string) {
            return Number(value).toLocaleString('es-EC')
          },
        },
      },
    },
  }

  return (
    <>
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }
        .filter-btn.active {
          background-color: #05457e !important;
          color: white !important;
        }
        .filter-btn.active:hover {
          background-color: #043a6b !important;
        }
        .filter-btn:not(.active):hover {
          background-color: #e5e7eb;
        }
      `}</style>
      <div className="bg-gray-100 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <img src={LOGO_URL} alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                  <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href="https://enterprise.tonalstrategies.com/pricing/" target="_blank" rel="noreferrer"
                   className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:opacity-90" style={{ backgroundColor: '#05457e' }}>
                  Calculador
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Filtro de Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex items-center flex-shrink-0">
                <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por especialidad:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleEspecialidad('todas')}
                  className={`filter-btn ${especialidadTodas ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700`}
                >
                  Todas
                </button>
                {especialidades.map((esp, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleEspecialidad(esp.nombre)}
                    className={`filter-btn ${espActiva === esp.nombre ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700`}
                  >
                    {esp.nombre} <span className="text-xs opacity-70">({esp.total})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Filtro de Ciudad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex items-center flex-shrink-0">
                <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por ciudad:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleCiudad('todas')}
                  className={`filter-btn ${todasSeleccionadas ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700`}
                >
                  Todas
                </button>
                {segmentos
                  .filter(seg => disponibles.has(seg.ciudad))
                  .map((seg, idx) => (
                    <button
                      key={idx}
                      onClick={() => toggleCiudad(seg.ciudad)}
                      className={`filter-btn ${filtros.includes(seg.ciudad) ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700`}
                    >
                      {seg.ciudad}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Estadisticas Generales - Colapsable */}
          <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-lg p-4 lg:p-6 border border-sky-200 mb-6">
            <button
              onClick={() => setResumenExpanded(!resumenExpanded)}
              className="w-full text-left focus:outline-none py-1 cursor-pointer"
            >
              <h2 className="font-semibold text-gray-900 flex items-center justify-between text-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>{tituloResumen}</span>
                  <span className="text-sm font-normal text-gray-500 ml-3">{totalMostrado.toLocaleString('es-EC')} {etiqueta}</span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform duration-300`}
                  style={{ transform: resumenExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </h2>
            </button>

            <div
              ref={resumenRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: resumenExpanded ? undefined : 0, opacity: resumenExpanded ? 1 : 0 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                  <p className="text-3xl lg:text-4xl font-bold text-gray-900">{totalMostrado.toLocaleString('es-EC')}</p>
                  <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                  <p className="text-3xl lg:text-4xl font-bold" style={{ color: '#05457e' }}>{especialidadesMostradas}</p>
                  <p className="text-sm text-gray-600 mt-1">Especialidades</p>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                  <p className="text-3xl lg:text-4xl font-bold" style={{ color: '#05457e' }}>{ciudadesMostradas}</p>
                  <p className="text-sm text-gray-600 mt-1">Ciudades</p>
                </div>
              </div>
            </div>
          </div>

          {/* Graficas */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            {/* Distribución por Especialidad */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
                Distribución por Especialidad
              </h3>
              <div style={{ height: `${Math.max(140, espLabels.length * 70)}px` }}>
                <Bar data={espBarData} options={espBarOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>

            {/* Distribución por Ciudad */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Distribución por Ciudad
              </h3>
              <div style={{ height: '500px' }}>
                <Bar data={barData} options={barOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>
          </div>

          {/* Top Ciudades */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Top Ciudades
            </h3>
            <div className="space-y-4 mt-6">
              {segFiltrados.map((seg, idx) => {
                const porcentaje = pct(seg.total, totalMostrado)
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{seg.ciudad}</span>
                      <span className="text-gray-500">{seg.total.toLocaleString('es-EC')} ({porcentaje}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ width: `${porcentaje}%`, backgroundColor: '#05457e' }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Tabla de Detalle */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" style={{ color: '#05457e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-base lg:text-lg">Detalle por Ciudad</span>
                  <span className="text-xs lg:text-sm text-gray-500 ml-2">({ciudadesMostradas} ciudades)</span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${expanded ? 'expanded' : 'animate-pulse-subtle'}`}
                  style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <div
              ref={contentRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ciudad</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {segFiltrados.map((seg, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{seg.ciudad}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold" style={{ color: '#05457e' }}>{seg.total.toLocaleString('es-EC')}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500">{pct(seg.total, totalMostrado)}%</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{totalMostrado.toLocaleString('es-EC')}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center">
          <div className="text-xs text-gray-400">
            <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
              Tonal Enterprise
            </a>
            <span className="mx-2">&bull;</span>
            <span>2026 Tonal Strategies</span>
          </div>
        </footer>
      </div>
    </>
  )
}
